import { LitElement } from "lit"
import { filter, Subject, takeUntil } from "rxjs"
import { manageStickyElements, StickyElementRefs, stickyNotifiers } from './RpsStickyElements'
import styles from './rps-sticky-header.pcss'

export default class RpsStickyHeader extends LitElement {
  private didScroll = false

  private lastScrollTop = 0

  private readonly delta = 5

  private unsubscribe$ = new Subject<void>()

  private floatingHeaders: HTMLElement[] = []

  private floatingHeadersMap = new Map<HTMLElement, StickyElementRefs>()

  private stickyElements: HTMLElement[] = []

  private stickyElementRefs = new Map<HTMLElement, StickyElementRefs>()

  private stickyQueue: Set<HTMLElement> = new Set()

  private refreshInterval!: number

  private mutationObserver!: MutationObserver

  constructor() {
    super()

    const styleEl = this.ownerDocument.createElement('style')
    this.ownerDocument.head.appendChild(styleEl)
    styleEl.appendChild(this.ownerDocument.createTextNode(styles))
  }

  createRenderRoot() {
    return this;
  }

  shouldUpdate() {
    return false
  }

  connectedCallback() {
    super.connectedCallback()

    const { floatingHeaders, stickyElements } = stickyNotifiers

    floatingHeaders.pipe(
      filter(map => map.size > 0),
      takeUntil(this.unsubscribe$),
    ).subscribe(elMap => {
      const elements = [
        ...new Set([...this.floatingHeaders, ...elMap.keys()]),
      ]
      // eslint-disable-next-line no-bitwise
      elements.sort((el1, el2) => el1.compareDocumentPosition(el2) & Node.DOCUMENT_POSITION_FOLLOWING)
      this.floatingHeaders = elements

      this.floatingHeadersMap = new Map([
        ...this.floatingHeadersMap, ...elMap
      ])
      this.registerHeaders()
      manageStickyElements(this.ownerDocument)
    })

    stickyElements.pipe(
      filter(map => map.size > 0),
      takeUntil(this.unsubscribe$),
    ).subscribe(stickyMap => {
      const elements = [
        ...new Set([...this.stickyElements, ...stickyMap.keys()])
      ]
      // eslint-disable-next-line no-bitwise
      elements.sort((el1, el2) => el1.compareDocumentPosition(el2) & Node.DOCUMENT_POSITION_FOLLOWING)
      this.stickyElements = elements
      this.stickyElementRefs = new Map([
        ...this.stickyElementRefs, ...stickyMap
      ])
      this.manageStickyElements()
    })

    this.mutationObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        const childs = mutation.addedNodes
        childs.forEach(child => {
          const el = child as HTMLElement
          const idx = this.stickyElements.indexOf(el)
          if (idx >= 0) {
            const newEl = this.stickyElements[idx + 1]
            if (this.stickyQueue.has(newEl)) {
              el.insertAdjacentElement('afterend', newEl)
              this.stickyQueue.delete(newEl)
            }
          }
        })
      }
    })
    this.mutationObserver.observe(this, { childList: true })

    this.ownerDocument.addEventListener('scroll', this.scrollManager, { passive: true })
    this.ownerDocument.addEventListener('reset-sticky', this.resetSticky)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.unsubscribe$.next()
    this.unsubscribe$.complete()

    this.ownerDocument.removeEventListener('scroll', this.scrollManager)
    this.ownerDocument.removeEventListener('reset-sticky', this.resetSticky)

    this.mutationObserver.disconnect()
    clearInterval(this.refreshInterval)
    for (const [, ref] of this.floatingHeadersMap) {
      ref.observers?.forEach(obs => {
        obs.disconnect()
      })
    }
    for (const [, ref] of this.stickyElementRefs) {
      ref.observers?.forEach(obs => {
        obs.disconnect()
      })
    }
  }

  private registerHeaders() {
    this.manageScrollHeader()

    this.refreshInterval = window.setInterval(() => {
      if (this.didScroll) {
        this.hasScrolled()
        this.didScroll = false
      }
    }, 250)
  }

  private async manageScrollHeader() {
    for (const el of this.floatingHeaders) {
      const refs = this.floatingHeadersMap.get(el)!
      if (refs?.observers) {
        // eslint-disable-next-line no-continue
        continue
      }
      if (!refs.parentElement) {
        // eslint-disable-next-line no-continue
        continue
      }
      const callback: IntersectionObserverCallback = entries => {
        for (const entry of entries) {
          if (entry.intersectionRatio >= 1 && entry.isIntersecting) {
            refs.parentElement.append(el)
          } else if (entry.intersectionRatio <= 0 && !entry.isIntersecting) {
            const idx = this.floatingHeaders.indexOf(el)
            if (idx === 0) {
              this.prepend(el)
            } else if (this.floatingHeaders[idx - 1].parentElement === this) {
              this.append(el)
            }
          }
        }
      }
      const obs = new IntersectionObserver(callback, { threshold: [0, 1] })
      obs.observe(refs.parentElement)
      refs.observers = [obs]
    }
  }

  private scrollManager = () => {
    this.didScroll = true
  }

  private hasScrolled() {
    const st = this.ownerDocument.documentElement.scrollTop
    const { lastScrollTop, delta } = this
    if (Math.abs(st - lastScrollTop) <= delta) {
      return
    }
    const height = Array.from(
      this.querySelectorAll<HTMLElement>('[floating-header]')
    ).reduce((sum, el) => {
      const ref = this.floatingHeadersMap.get(el)!
      const tot = sum + ref.rect.height
      return tot
    }, 0)
    const dy = height ? `-${height}px` : '-100%'
    if (st >= lastScrollTop) {
      this.style.setProperty('transform', `translate3d(0, ${dy}, 0)`);
    } else {
      this.style.visibility = st <= 68 ? 'hidden' : 'visible';
      this.style.removeProperty('transform');
    }
    this.lastScrollTop = st
  }

  private manageStickyElements() {
    const headerHeight = this.floatingHeaders.reduce((sum, el) => {
      const ref = this.floatingHeadersMap.get(el)!
      const tot = sum + ref.rect.height
      return tot
    }, 0)
    for (const el of this.stickyElements) {
      const refs = this.stickyElementRefs.get(el)
      if (!refs || !refs.sentinel || !!refs.observers) {
        // eslint-disable-next-line no-continue
        continue /* already used */
      }
      const idx = this.stickyElements.indexOf(el)
      /* dimensioni necessarie per l'interception observer */
      const rootHeight = this.stickyElements.slice(0, idx).reduce((sum, elem) => {
        const r = this.stickyElementRefs.get(elem)!
        const tot = sum + r.rect.height
        return tot
      }, 0)
      const { height: elHeight, width: elWidth } = refs.rect
      const placeholder = this.ownerDocument.createElement('div')
      placeholder.classList.add('header-placeholder')
      placeholder.style.setProperty('width', `${elWidth}px`)
      placeholder.style.setProperty('height', `${elHeight}px`)
      refs.placeholder = placeholder
      /* questo observer controlla l'uscita dell'elemento dalla viewport */
      const exitObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            /* sostituisce l'elemento originale con il placeholder */
            if (el.parentElement !== this) {
              el.replaceWith(placeholder)
            }
            /* se è il primo elemento, lo appende in coda */
            let refElement: HTMLElement
            if (idx === 0) {
              refElement = this.floatingHeaders[
                this.floatingHeaders.length -1
              ]
            } else {
              refElement = this.stickyElements[idx - 1]
            }
            if (refElement.parentElement === this) {
              refElement.insertAdjacentElement('afterend', el)
            } else {
              this.stickyQueue.add(el)
            }
          }
        }
      }, { threshold: [0], rootMargin: `${-(rootHeight - 10)}px 0px 0px 0px` })
      exitObserver.observe(refs.sentinel)
      /* questo observer controlla l'ingresso dell'elemento dalla viewport */
      const enterObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            placeholder.replaceWith(el)
          }
        }
      }, { threshold: [0], rootMargin: `-${rootHeight + headerHeight}px 0px 0px 0px` })
      enterObserver.observe(refs.sentinel)
      /* salva gli observer per poterli rimuovere alla fine */
      refs.observers = [exitObserver, enterObserver]
    }
  }

  private resetSticky = () => {
    for (const el of this.stickyElements) {
      const refs = this.stickyElementRefs.get(el)
      refs?.placeholder?.replaceWith(el)
    }
    for (const [, ref] of this.stickyElementRefs) {
      ref.observers?.forEach(obs => {
        obs.disconnect()
      })
    }
    this.stickyElements = []
    this.stickyElementRefs = new Map()
  }
}

if (customElements.get('rps-sticky-header') == null) {
  customElements.define('rps-sticky-header', RpsStickyHeader)
}

declare global {
  interface HTMLElementTagNameMap {
    'rps-sticky-header': RpsStickyHeader
  }
}
